import { Mail } from 'lucide-react';

import { useEmailLogs } from '@/hooks/useEmailLogs';
import cn from '@/utils/cn';
import { formatDateTime } from '@/utils/format';

interface LeadEmailLogListProps {
  leadId: number;
  limit?: number;
}

const STATUS_TONE: Record<string, string> = {
  sent: 'text-success',
  delivered: 'text-success',
  failed: 'text-danger',
  bounced: 'text-danger',
  complained: 'text-warning',
};

// Compact, lead-scoped recent-email list shown under the stepper email composer
// (reuses the same /email-logs endpoint, filtered by leadId).
export function LeadEmailLogList({ leadId, limit = 5 }: LeadEmailLogListProps) {
  const { data, isLoading } = useEmailLogs(
    { leadId, limit },
    Number.isFinite(leadId) && leadId > 0,
  );
  const rows = data?.rows ?? [];

  if (isLoading || rows.length === 0) return null;

  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Mail className="h-3.5 w-3.5" />
        Emails sent for this lead
      </div>
      <ul className="flex flex-col divide-y divide-border">
        {rows.map((log) => (
          <li
            key={log.id}
            className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 py-1.5 text-xs"
          >
            <span className="min-w-0 flex-1 truncate" title={log.subject}>
              {log.subject}
            </span>
            {log.referenceNumber && (
              <span className="font-mono text-[11px] text-muted-foreground">
                {log.referenceNumber}
              </span>
            )}
            <span className="whitespace-nowrap text-muted-foreground">
              {formatDateTime(log.sentAt ?? log.createdAt)}
            </span>
            <span
              className={cn('font-semibold', STATUS_TONE[log.status] ?? 'text-muted-foreground')}
            >
              {log.status}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
